import { Checkbox, Col, message, Row } from 'antd';
import config from 'config';
import { format } from 'date-fns';
import {
  useGetProductReportsLazyQuery,
  useGetWoocommerceCategoriesQuery,
} from 'gql/types';
import { Button } from 'modules/common/components';
import { useChunkedLoader } from 'modules/common/hooks/useChunkedLoader';
import { ICursorPaginatedInput } from 'modules/common/utils/loadCursorPaginatedData';
import { getProductCategoriesMap } from 'modules/products/utils/getProductCategoriesMap';
import { useReportFilterOptions } from 'modules/reports/hooks/useReportFilterOptions';
import { useReportFilters } from 'modules/reports/hooks/useReportFilters';
import { PartialProductReport } from 'modules/reports/types';
import { generateReportHeaders } from 'modules/reports/utils/generateReportHeaders';
import { mapReportData } from 'modules/reports/utils/mapReportData';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getProductReportColumns } from './columns';
import { getProductReportFilters } from './filters';

import { ReportDownloads } from '../shared/ReportDownloads';
import { ReportFilterBar } from '../shared/ReportFilterBar';

const ProductReports: FC = () => {
  const { t } = useTranslation('reports.ProductReports');
  const [getReportsChunk] = useGetProductReportsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const woocommerceCategoriesQuery = useGetWoocommerceCategoriesQuery({
    variables: { input: {} },
  });

  const [mantelOnly, setMantelOnly] = useState(false);

  const fetchReportsChunk = useCallback(
    async (input: ICursorPaginatedInput) => {
      const result = await getReportsChunk({
        variables: { input: { ...input, mantelOnly } },
      });
      if (result.error) {
        throw Error(result.error.message);
      }
      return {
        meta: result.data?.getProductReports.meta,
        data: (result.data?.getProductReports.data ??
          []) as PartialProductReport[],
      };
    },
    [mantelOnly, getReportsChunk]
  );

  const {
    isLoading,
    data: reports,
    load: loadReports,
    reset: resetReports,
  } = useChunkedLoader({
    query: fetchReportsChunk,
    onError: (error) => message.error(error.message),
    onEmpty: () => message.info(t('emptyReport')),
  });

  useEffect(() => {
    resetReports();
  }, [mantelOnly, resetReports]);

  const fileName = useMemo(() => {
    const prefix = mantelOnly ? 'products_mantel' : 'products';
    return `${prefix}_${format(new Date(), config.DATE_FORMATS.SHORT_DATE)}`;
  }, [mantelOnly]);

  const columns = useMemo(() => getProductReportColumns(t), [t]);
  const headers = useMemo(() => generateReportHeaders(columns), [columns]);

  const categoriesMap = useMemo(() => {
    const categories =
      woocommerceCategoriesQuery.data?.getWoocommerceCategories;
    return categories ? getProductCategoriesMap(categories) : undefined;
  }, [woocommerceCategoriesQuery.data]);

  const filterOptions = useReportFilterOptions();
  const categoryNames = useMemo(
    () => Object.values(categoriesMap ?? {}).sort((a, b) => a.localeCompare(b)),
    [categoriesMap]
  );
  const filterConfigs = useMemo(
    () => getProductReportFilters(t, filterOptions, categoryNames),
    [t, filterOptions, categoryNames]
  );
  const filterContext = useMemo(() => ({ categoriesMap }), [categoriesMap]);

  const {
    values,
    setFilterValue,
    clearFilters,
    optionsByKey,
    filteredData,
    hasActiveFilters,
  } = useReportFilters(reports, filterConfigs, filterContext);

  const mappedData = useMemo(
    () => mapReportData(filteredData ?? [], columns, filterContext),
    [filteredData, columns, filterContext]
  );

  return (
    <Row gutter={[10, 10]}>
      <Col span={24}>
        <Row gutter={[16, 0]} align="middle">
          <Col>
            <Button
              onClick={loadReports}
              loading={isLoading}
              disabled={isLoading}
              type="primary"
            >
              {t('generateCsvReport')}
            </Button>
          </Col>
          <Col>
            <Checkbox
              checked={mantelOnly}
              onChange={(e) => setMantelOnly(e.target.checked)}
              disabled={isLoading}
            >
              {t('mantelAssignedOnly')}
            </Checkbox>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <ReportFilterBar
          configs={filterConfigs}
          values={values}
          optionsByKey={optionsByKey}
          onChange={setFilterValue}
          onClear={clearFilters}
          hasActiveFilters={hasActiveFilters}
          counts={
            reports
              ? {
                  shown: filteredData?.length ?? 0,
                  total: reports.length,
                }
              : undefined
          }
        />
      </Col>
      <Col span={24}>
        <ReportDownloads
          fileName={fileName}
          headers={headers}
          data={mappedData}
        />
      </Col>
    </Row>
  );
};

export default ProductReports;
